import { useEffect, useState } from "react";
import { Button, FormControl, TextField, Typography, Box, CircularProgress, Chip } from "@mui/material";
import { useGetNotesByArguments } from "../../../../hooks/useGetNotesByArguments";
import { useAuth } from "../../../../context/AuthContext";
import { useFilter } from "../../../../context/FilterContext";

export const TabEditNotes = ({ notaSeleccionada }) => {
  const { token } = useAuth();
  const { setEditedNotaData } = useFilter();

  const { note, loading, error, fetchNote } = useGetNotesByArguments(token);
  
  const [editedData, setEditedData] = useState({
    titulo: '',
    autor: '',
    fecha: '',
    url: '',
    urlTestigo: '',
    contenido: '',
    keywords: ''
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  const getWordCount = (htmlStr) => {
    if (!htmlStr) return 0;
    const text = htmlStr.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text === "" ? 0 : text.split(" ").length;
  };

  useEffect(() => {
    if (notaSeleccionada?.CAPCLAVE && notaSeleccionada.CAPCLAVE !== note?.CAPCLAVE) {
      fetchNote({
        capclave: notaSeleccionada.CAPCLAVE,
        articleWords: notaSeleccionada?.articleWords || "",
      });
    }
  }, [notaSeleccionada?.CAPCLAVE, notaSeleccionada?.articleWords, fetchNote, note?.CAPCLAVE]);

  useEffect(() => {
    if (note && notaSeleccionada) {
      const initialTitulo = note.CAPTITULO || notaSeleccionada.CAPTITULO || '';
      const initialContenido = note.CAPTEXTCOMP || '';
      
      setEditedData({
        titulo: initialTitulo,
        autor: note.fuente?.AUTOR || note.fuente?.FUENTE || '',
        fecha: note.fuente?.FECHAHORA ? `${note.fuente.FECHA} ${note.fuente.FECHAHORA}` : '',
        url: note.capurloriginal || '',
        urlTestigo: note.fuente?.URL || '',
        contenido: initialContenido,
        keywords: note.articleWords || ''
      });
      
      setWordCount(getWordCount(initialContenido));
      
      setEditedNotaData({
        titulo: initialTitulo,
        contenido: initialContenido,
      });
      
      setHasChanges(false);
    }
  }, [note, notaSeleccionada, setEditedNotaData]);

  const handleFieldChange = (field, value) => {
    setEditedData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'titulo' || field === 'contenido') {
        setEditedNotaData(prevContext => ({ ...prevContext, [field]: value }));
      }
      return newData;
    });
    setHasChanges(true);
  };

  const handleReset = () => {
    if (note && notaSeleccionada) {
      const initialTitulo = note.CAPTITULO || notaSeleccionada.CAPTITULO || '';
      const initialContenido = note.CAPTEXTCOMP || '';
      
      setEditedData({
        titulo: initialTitulo,
        autor: note.fuente?.AUTOR || note.fuente?.FUENTE || '',
        fecha: note.fuente?.FECHAHORA ? `${note.fuente.FECHA} ${note.fuente.FECHAHORA}` : '',
        url: note.capurloriginal || '',
        urlTestigo: note.fuente?.URL || '',
        contenido: initialContenido,
        keywords: note.articleWords || ''
      });
      
      setWordCount(getWordCount(initialContenido));
      
      setEditedNotaData({
        titulo: initialTitulo,
        contenido: initialContenido,
      });
      
      setHasChanges(false);
    }
  };

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      "& fieldset": { border: "none" },
    },
    "& .MuiInputBase-input": { padding: "10px 14px", fontSize: "15px", color: "#1a1a1a" },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#1a1a1a" },
  };

  const commonLabelStyle = {
    fontSize: "14px",
    color: "#235789",
    fontWeight: "700",
    margin: "0 0 4px 0",
  };

  return (
    <Box sx={{
      height: '100%',
      overflowY: 'auto',
      paddingRight: '8px',
    }}>
      <FormControl fullWidth>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando contenido de la nota...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
            <Typography color="error">Error al cargar la nota: {error.message}</Typography>
          </Box>
        ) : note ? (
          <>
            <Box sx={{ mb: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" color="primary">
                Mostrando contenido detallado para: {notaSeleccionada?.CAPTITULO?.substring(0, 100)}...
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <p style={commonLabelStyle}>Título</p>
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => handleFieldChange('titulo', e.currentTarget.innerHTML)}
                style={{
                  ...commonTextFieldStyles["& .MuiOutlinedInput-root"],
                  ...commonTextFieldStyles["& .MuiInputBase-input"],
                  minHeight: '40px',
                  padding: '10px 14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  lineHeight: '1.5',
                  wordBreak: 'break-word',
                  outline: 'none',
                  backgroundColor: '#fff',
                }}
                dangerouslySetInnerHTML={{ __html: editedData.titulo }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <p style={commonLabelStyle}>Autor/Fuente</p>
              <TextField
                variant="outlined"
                fullWidth
                value={editedData.autor}
                onChange={(e) => handleFieldChange('autor', e.target.value)}
                sx={commonTextFieldStyles}
                disabled
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <p style={commonLabelStyle}>Fecha y Hora</p>
              <TextField
                variant="outlined"
                fullWidth
                value={editedData.fecha}
                onChange={(e) => handleFieldChange('fecha', e.target.value)}
                sx={commonTextFieldStyles}
                disabled
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <p style={commonLabelStyle}>URL Original</p>
              <TextField
                variant="outlined"
                fullWidth
                value={editedData.url}
                onChange={(e) => handleFieldChange('url', e.target.value)}
                sx={commonTextFieldStyles}
                disabled
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <p style={commonLabelStyle}>URL Testigo (Cápsula)</p>
              {editedData.urlTestigo ? (
                <Box
                  component="a"
                  href={editedData.urlTestigo}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ...commonTextFieldStyles["& .MuiOutlinedInput-root"],
                    ...commonTextFieldStyles["& .MuiInputBase-input"],
                    display: 'block',
                    color: '#235789',
                    textDecoration: 'none',
                    wordBreak: 'break-all',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {editedData.urlTestigo}
                </Box>
              ) : (
                <TextField
                  variant="outlined"
                  fullWidth
                  value=""
                  sx={commonTextFieldStyles}
                  disabled
                />
              )}
            </Box>

            <Box sx={{ mb: 1, position: "relative" }}>
              <p style={commonLabelStyle}>Contenido de la Nota</p>
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => handleFieldChange('contenido', e.currentTarget.innerHTML)}
                onInput={(e) => setWordCount(getWordCount(e.currentTarget.innerHTML))}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "12px 14px 40px 14px",
                  minHeight: "150px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  background: "#fff",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  lineHeight: "1.6",
                  outline: "none"
                }}
                dangerouslySetInnerHTML={{ __html: editedData.contenido }}
              />
              <Chip
                size="small"
                label={`${wordCount} palabras`}
                sx={{
                  position: "absolute",
                  bottom: "10px",
                  right: "12px",
                  backgroundColor: "#f5f5f5"
                }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <p style={commonLabelStyle}>Keywords de Búsqueda</p>
              <TextField
                variant="outlined"
                fullWidth
                value={editedData.keywords}
                onChange={(e) => handleFieldChange('keywords', e.target.value)}
                sx={commonTextFieldStyles}
                disabled
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleReset}
                disabled={!hasChanges}
                style={{ padding: "6px", borderRadius: "8px", fontSize: "13px" }}
              >
                <span>Descartar cambios</span>
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="textSecondary">
              Selecciona una nota para ver su contenido detallado
            </Typography>
          </Box>
        )}
      </FormControl>
    </Box>
  );
};